/**
 * Таблица с клавиатурной навигацией — основа всех повторяющихся блоков
 * (ТЗ FR-11, п. 8.4).
 *
 * Требование звучит скромно, но именно от него зависит, окупится ли проект:
 * юрист заполняет кредиторов, детей, шесть разделов имущества и сделки.
 * Если для каждой строки нужно тянуться к мыши, экономия времени исчезает.
 *
 * Клавиши:
 *   Tab / Shift+Tab  — следующая и предыдущая ячейка, на краю строки переход
 *                      на соседнюю строку;
 *   Enter            — новая строка (в последней строке) либо переход вниз;
 *   ↑ ↓              — строка выше и ниже в той же графе;
 *   Ctrl+Enter       — вставить строку сразу после текущей;
 *   Ctrl+Delete      — удалить текущую строку;
 *   Escape           — отменить правку ячейки.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export interface ColumnDef<Row> {
  key: string;
  title: string;
  /**
   * Полное пояснение к столбцу — во всплывающей подсказке.
   *
   * Нужно там, где столбец узкий, а название длинное: столбец с флажком
   * шириной в сам флажок не вмещает «Просрочка более трёх месяцев»,
   * и заголовок ломается посреди слова. Короткое название в шапке,
   * полное — в подсказке.
   */
  hint?: string;
  /** Ширина в процентах */
  width?: number;
  /**
   * Тип ячейки: обычный ввод, число, дата, признак, выбор из списка
   * либо список с возможностью вписать своё (`combo`).
   *
   * Разница между `select` и `combo` существенная. `select` — там, где
   * перечень закрыт: вид собственности и подвиды имущества заданы формой
   * приказа № 530, и в профиле хранится КОД. Вписать туда своё нельзя —
   * значение просто не с чем сопоставить.
   *
   * `combo` — там, где справочник по природе неполон: содержание
   * обязательства, основание, подтверждающий документ. Такой список
   * не перечисляет всё, что бывает, и юрист должен иметь возможность
   * вписать своё, не заводя запись в справочник ради одного дела.
   */
  type?: 'text' | 'money' | 'number' | 'date' | 'checkbox' | 'select' | 'combo' | 'multiline';
  options?: string[];
  /** Чтение значения из строки */
  get: (row: Row) => string | number | boolean;
  /** Запись значения в строку */
  set: (row: Row, value: string) => Row;
  /** Собственная отрисовка ячейки — для полей с подсказками */
  render?: (props: { row: Row; rowIndex: number; onChange: (row: Row) => void; onKeyDown: (e: React.KeyboardEvent) => void; readOnly: boolean }) => ReactNode;
  /** Ячейка не редактируется (например, расчётный итог) */
  readOnly?: boolean;
  align?: 'left' | 'right' | 'center';
}

export interface DataGridProps<Row> {
  rows: Row[];
  columns: Array<ColumnDef<Row>>;
  onChange: (rows: Row[]) => void;
  /** Создание пустой строки */
  createRow: () => Row;
  getRowId: (row: Row) => string;
  readOnly?: boolean;
  /** Сообщение при пустой таблице */
  emptyText?: string;
  /** Итоговая строка под таблицей */
  footer?: ReactNode;
  /** Ошибки по строкам: индекс строки → ключи граф с ошибкой */
  errors?: Record<number, string[]>;
}

/**
 * Ячейка, переносящая длинный текст по строкам.
 *
 * Наименование кредитора и адрес — сорок-шестьдесят знаков: «ООО
 * „Профессиональная коллекторская организация…“», почтовый адрес с индексом,
 * корпусом и офисом. В однострочной графе от них видна первая треть, а сверять
 * их приходится с договором — построчно и глазами.
 *
 * Расширять таблицу до нужной ширины бессмысленно: граф десять, и любое
 * наименование всё равно окажется длиннее. Поэтому текст переносится,
 * а строка становится выше ровно настолько, насколько нужно.
 *
 * Enter остаётся за таблицей — он создаёт НОВУЮ СТРОКУ ДЕЛА, как и в других
 * графах. Переносить текст вручную здесь незачем: он переносится сам
 * по ширине, а разнобой в способе ввода между графами сбивал бы с толку.
 */
function GrowingCell({
  value,
  readOnly,
  onChange,
  onKeyDown,
}: {
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Высота считается после отрисовки: до неё содержимое ещё не измерить
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      className="grid-multiline"
      rows={1}
      value={value}
      title={value}
      readOnly={readOnly}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        // Перевод строки внутри ячейки не нужен: Enter принадлежит таблице
        if (event.key === 'Enter') event.preventDefault();
        onKeyDown(event);
      }}
    />
  );
}

/**
 * Денежная ячейка.
 *
 * Значение в профиле хранится копейками, а на экран выводится
 * отформатированным: «1 250 000,00». Если форматировать его на каждое
 * нажатие, ввод становится невозможным: «6» превращается в «6,00», следующая
 * цифра даёт «6,000», что читается как те же шесть рублей. Больше одного
 * знака в графу было не ввести — а это графы задолженности, главные в деле.
 *
 * Поэтому, пока в графе печатают, показывается ровно набранное. Форматирование
 * применяется при уходе из графы. Тот же приём использован в обычных формах
 * (`MoneyField`), здесь он повторён для таблицы.
 */
function AmountCell({
  value,
  readOnly,
  onChange,
  onKeyDown,
}: {
  /** Отформатированное значение из строки */
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}) {
  const [typed, setTyped] = useState<string | null>(null);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={typed ?? value}
      readOnly={readOnly}
      onChange={(event) => {
        /*
          Из суммы отбрасывается всё, кроме цифр, запятой, точки и пробелов:
          разряды разделяют пробелом, и он же приезжает при вставке суммы,
          скопированной из документа, — в том числе неразрывный.
          Знак минуса не нужен: отрицательной задолженности не бывает.
        */
        const raw = event.target.value.replace(/[^\d,.\s]/g, '');
        setTyped(raw);
        onChange(raw);
      }}
      onBlur={() => setTyped(null)}
      onKeyDown={onKeyDown}
    />
  );
}

export function DataGrid<Row>({
  rows,
  columns,
  onChange,
  createRow,
  getRowId,
  readOnly = false,
  emptyText = 'Строк нет. Нажмите «Добавить строку» или Enter в таблице.',
  footer,
  errors = {},
}: DataGridProps<Row>) {
  const tableRef = useRef<HTMLTableElement>(null);
  /*
    Свой набор подсказок у каждой таблицы. На странице их несколько
    (кредиторы и обязательные платежи), а связь поля со списком идёт
    по идентификатору: совпади он — во втором поле всплывал бы чужой перечень.
  */
  const gridId = useId();
  /** Куда поставить курсор после перерисовки: строка и графа */
  const [focusTarget, setFocusTarget] = useState<{ row: number; col: number } | null>(null);

  const focusCell = useCallback((rowIndex: number, colIndex: number) => {
    const cell = `[data-cell="${rowIndex}-${colIndex}"]`;
    const selector = `${cell} input, ${cell} select, ${cell} textarea`;
    const element = tableRef.current?.querySelector<HTMLElement>(selector);
    element?.focus();
    if (element instanceof HTMLInputElement && element.type === 'text') element.select();
  }, []);

  // Перевод курсора выполняется после отрисовки: новая строка ещё не в DOM
  // в момент нажатия Enter.
  useEffect(() => {
    if (!focusTarget) return;
    focusCell(focusTarget.row, focusTarget.col);
    setFocusTarget(null);
  }, [focusTarget, focusCell]);

  const updateRow = (index: number, row: Row) => {
    const next = rows.slice();
    next[index] = row;
    onChange(next);
  };

  const insertRow = (afterIndex: number) => {
    const next = rows.slice();
    next.splice(afterIndex + 1, 0, createRow());
    onChange(next);
    setFocusTarget({ row: afterIndex + 1, col: 0 });
  };

  const removeRow = (index: number) => {
    const next = rows.slice();
    next.splice(index, 1);
    onChange(next);
    setFocusTarget({ row: Math.max(0, index - 1), col: 0 });
  };

  const handleKeyDown = (event: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
    if (readOnly) return;

    // Ctrl+Enter — вставка строки, Ctrl+Delete — удаление.
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault();
      insertRow(rowIndex);
      return;
    }
    if (event.ctrlKey && (event.key === 'Delete' || event.key === 'Backspace')) {
      event.preventDefault();
      removeRow(rowIndex);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (rowIndex === rows.length - 1) insertRow(rowIndex);
      else setFocusTarget({ row: rowIndex + 1, col: colIndex });
      return;
    }

    if (event.key === 'ArrowDown' && rowIndex < rows.length - 1) {
      event.preventDefault();
      setFocusTarget({ row: rowIndex + 1, col: colIndex });
      return;
    }
    if (event.key === 'ArrowUp' && rowIndex > 0) {
      event.preventDefault();
      setFocusTarget({ row: rowIndex - 1, col: colIndex });
      return;
    }

    if (event.key === 'Tab') {
      // На краю строки Tab переносит на соседнюю строку, а не выбрасывает
      // из таблицы: иначе ввод прерывается на каждой строке.
      const last = columns.length - 1;
      if (!event.shiftKey && colIndex === last && rowIndex < rows.length - 1) {
        event.preventDefault();
        setFocusTarget({ row: rowIndex + 1, col: 0 });
      } else if (event.shiftKey && colIndex === 0 && rowIndex > 0) {
        event.preventDefault();
        setFocusTarget({ row: rowIndex - 1, col: last });
      }
    }
  };

  return (
    <div className="grid">
      {/*
        Списки подсказок объявляются один раз на таблицу, а не в каждой
        ячейке: строк бывают десятки, и повторять один и тот же перечень
        в каждой — лишняя разметка на ровном месте.
      */}
      {columns
        .filter((column) => column.type === 'combo')
        .map((column) => (
          <datalist key={column.key} id={`${gridId}-${column.key}`}>
            {(column.options ?? []).map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        ))}

      <table ref={tableRef} className="grid-table">
        <thead>
          <tr>
            <th className="grid-num">№</th>
            {columns.map((column) => (
              <th
                key={column.key}
                style={column.width ? { width: `${column.width}%` } : undefined}
                title={column.hint}
              >
                {column.title}
              </th>
            ))}
            <th className="grid-actions" aria-label="Действия со строкой" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="grid-empty">
                {emptyText}
              </td>
            </tr>
          )}

          {rows.map((row, rowIndex) => {
            const rowErrors = errors[rowIndex] ?? [];
            return (
              <tr key={getRowId(row)} className={rowErrors.length ? 'grid-row-error' : undefined}>
                <td className="grid-num">{rowIndex + 1}</td>

                {columns.map((column, colIndex) => {
                  const invalid = rowErrors.includes(column.key);
                  const cellReadOnly = readOnly || column.readOnly === true;

                  return (
                    <td
                      key={column.key}
                      data-cell={`${rowIndex}-${colIndex}`}
                      className={invalid ? 'grid-cell-error' : undefined}
                      style={{ textAlign: column.align ?? 'left' }}
                    >
                      {column.render ? (
                        column.render({
                          row,
                          rowIndex,
                          onChange: (updated) => updateRow(rowIndex, updated),
                          onKeyDown: (event) => handleKeyDown(event, rowIndex, colIndex),
                          readOnly: cellReadOnly,
                        })
                      ) : column.type === 'checkbox' ? (
                        <input
                          type="checkbox"
                          checked={Boolean(column.get(row))}
                          disabled={cellReadOnly}
                          onChange={(e) => updateRow(rowIndex, column.set(row, e.target.checked ? 'true' : ''))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        />
                      ) : column.type === 'select' ? (
                        <select
                          value={String(column.get(row) ?? '')}
                          disabled={cellReadOnly}
                          onChange={(e) => updateRow(rowIndex, column.set(row, e.target.value))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        >
                          <option value="" />
                          {(column.options ?? []).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : column.type === 'money' || column.type === 'number' ? (
                        <AmountCell
                          value={String(column.get(row) ?? '')}
                          readOnly={cellReadOnly}
                          onChange={(value) => updateRow(rowIndex, column.set(row, value))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        />
                      ) : column.type === 'multiline' ? (
                        <GrowingCell
                          value={String(column.get(row) ?? '')}
                          readOnly={cellReadOnly}
                          onChange={(value) => updateRow(rowIndex, column.set(row, value))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        />
                      ) : column.type === 'combo' ? (
                        /*
                          Список подсказок при обычном поле ввода: выбрать
                          из справочника или вписать своё. Отдельного пункта
                          «иное» с появлением второго поля нет намеренно —
                          он добавляет шаг там, где достаточно начать печатать.
                        */
                        <input
                          type="text"
                          list={`${gridId}-${column.key}`}
                          value={String(column.get(row) ?? '')}
                          title={String(column.get(row) ?? '')}
                          readOnly={cellReadOnly}
                          onChange={(e) => updateRow(rowIndex, column.set(row, e.target.value))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        />
                      ) : (
                        /*
                          Значение целиком — во всплывающей подсказке.

                          Наименование кредитора и адрес длиннее своей графы
                          даже на широкой таблице: «ООО Профессиональная
                          коллекторская организация…» не поместится нигде.
                          Подсказка позволяет прочитать значение, не забираясь
                          в ячейку и не прокручивая её содержимое стрелками.
                        */
                        <input
                          type={column.type === 'date' ? 'date' : 'text'}
                          value={String(column.get(row) ?? '')}
                          title={column.type === 'date' ? undefined : String(column.get(row) ?? '')}
                          readOnly={cellReadOnly}
                          onChange={(e) => updateRow(rowIndex, column.set(row, e.target.value))}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        />
                      )}
                    </td>
                  );
                })}

                <td className="grid-actions">
                  <button
                    type="button"
                    className="link-button"
                    title="Удалить строку (Ctrl+Delete)"
                    disabled={readOnly}
                    onClick={() => removeRow(rowIndex)}
                  >
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="grid-footer">
        <button type="button" className="link-button" disabled={readOnly} onClick={() => insertRow(rows.length - 1)}>
          + Добавить строку
        </button>
        <span className="hint">Tab — следующая графа, Enter — новая строка, Ctrl+Delete — удалить</span>
        {footer}
      </div>
    </div>
  );
}
